import { copyUnits, getFilterUnits, getUnits } from 'core/crud/lessons.crud';
import { useTranslate } from 'core/hooks/useTranslate';
import * as coreEntities from 'core/store/index';
import { COURSE_TYPES } from 'core/utils/constants/course';
import { parseUnitsToAdd } from 'core/utils/parses';
import { removeElementFromArray, successResponse } from 'core/utils/utils';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const INITIAL_FILTER = {
  search: '',
  year: null,
  discipline: null,
  program: null,
};
const INITIAL_PAGINATION = { pageSize: 12, offset: 0, hasMore: false };

export default function useDialogUnit({ open, courseUnits, onClose, onCopied, times, activeTime }) {
  const { t } = useTranslate();

  const language = useSelector((state) => coreEntities.i18n.selectors.getLanguage(state));
  const course = useSelector((state) => coreEntities.courses.selectors.getCurrentCourse(state));

  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [units, setUnits] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [amountUnits, setAmountUnits] = useState(0);
  const [disableUnits, setDisableUnits] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTER);
  const [searchText, setSearchText] = useState('');
  const [disciplines, setDisciplines] = useState([]);
  const [levels, setLevels] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [selectedTime, setSelectedTime] = useState(times?.length === 1 ? times[0].guid : activeTime || 1);
  const [optionsTimes, setOptionsTimes] = useState(times);

  useEffect(() => {
    if (times) {
      setSelectedTime(activeTime || times[0]);
      if (times?.guid) {
        setOptionsTimes(
          times.map((time) => ({
            value: time.guid,
            label: time.name,
          }))
        );
      } else {
        setOptionsTimes([
          { value: times ? times[0] : null, label: t('course:First quarterly') },
          { value: times ? times[1] : null, label: t('course:Second quarterly') },
          { value: times ? times[2] : null, label: t('course:Third quarterly') },
        ]);
      }
    }
  }, [times]);

  useEffect(() => {
    setSelectedTime(activeTime);
  }, [activeTime]);

  useEffect(() => {
    if (open) getFilters();
  }, [open]);

  useEffect(() => {
    if (open) getSomeUnits(pagination.pageSize, 0, true);
  }, [filters.search, filters.year, filters.discipline, filters.program, open]);

  useEffect(() => {
    setFilters({ ...filters, search: searchText });
  }, [searchText]);

  useEffect(() => {
    reviewUnitsToDisable(units);
  }, [courseUnits, units]);

  async function getFilters() {
    const response = await getFilterUnits();

    if (successResponse(response)) {
      let theDisciplines = response.data.data.disciplines;
      theDisciplines = theDisciplines.map((discipline) => {
        return { ...discipline, name: t('disciplines:' + discipline.guid).startsWith('ed-') ? discipline.name : t('disciplines:' + discipline.guid) };
      });
      setDisciplines(theDisciplines);

      setPrograms(response.data.data.programs);
      let years = response.data.data.education_years.map((year) => {
        return { guid: year.guid, level: year.level.guid, name: t('education-years:' + year.guid) + ' ' + t('education-levels:' + year.level.guid) };
      });
      try {
        years.sort((a, b) => (parseInt(a.guid.replace('ey', '')) > parseInt(b.guid.replace('ey', '')) ? 1 : -1));
      } catch (ex) {}

      setLevels(years);
    }
  }

  async function getSomeUnits(pageSize, offset, clean = false) {
    setLoading(true);
    const response = await getUnits(pageSize, offset, filters.year, filters.discipline, filters.program, filters.search);

    if (successResponse(response)) {
      setPagination({ ...pagination, offset: response.data.data.offset, hasMore: response.data.data.left > 0 });
      let newUnits = parseUnitsToAdd(response.data.data.units, language);
      let allUnits = clean ? newUnits : [...units, ...newUnits];
      setUnits(allUnits);
      setAmountUnits(response.data.data.total);
    }

    setLoading(false);
  }

  async function onAddSelectedUnits() {
    setLoading(true);
    const response = await copyUnits(
      selectedUnits,
      course.type === COURSE_TYPES.REFERENCED ? course.course_template_guid : course.guid,
      `{\"trimestre_guid\":\"${selectedTime ? selectedTime : 'notguid'}\"}`
    );

    if (successResponse(response)) {
      onClose();
      onCopied();
      setSelectedUnits([]);
    }

    setLoading(false);
  }

  function loadMoreUnits() {
    if (!loading) {
      setPagination({ ...pagination, hasMore: false });
      let page = units.length;
      getSomeUnits(pagination.pageSize, page, false);
    }
  }

  /**
   * Disable the units who belongs to the current course
   * @param {The listed units} units
   */
  function reviewUnitsToDisable(units) {
    let unitsToDisable = units.filter((unit) => courseUnits.includes(unit.guid));
    unitsToDisable = unitsToDisable.map((unit) => unit.guid);
    setDisableUnits(unitsToDisable);
  }

  function onCloseDialog() {
    setFilters(INITIAL_FILTER);
    setSelectedUnits([]);
    onClose();
  }

  function onChangeSearch(newSearch) {
    setSearchText(newSearch);
  }

  function onSelectDiscipline(newDiscipline) {
    if (newDiscipline) {
      setFilters({ ...filters, discipline: newDiscipline.value });
    }
  }

  function onSelectLevel(newLevel) {
    if (newLevel) {
      setFilters({ ...filters, year: newLevel.value });
    }
  }

  function onSelectProgram(newProgram) {
    if (newProgram) {
      setFilters({ ...filters, program: newProgram.value });
    }
  }

  function onChangeUnitSelected(isSelected, unitGuid) {
    if (isSelected) setSelectedUnits([...selectedUnits, unitGuid]);
    else {
      let newSelection = removeElementFromArray(selectedUnits, unitGuid);
      setSelectedUnits([...newSelection]);
    }
  }

  function onChangeTimeSelected(newTime) {
    setSelectedTime(newTime.value);
  }

  return {
    t,
    loading,
    units,
    selectedUnits,
    amountUnits,
    disableUnits,
    disciplines,
    levels,
    programs,
    filters,
    hasMore: pagination.hasMore,
    loadMoreUnits,
    onCloseDialog,
    onChangeSearch,
    onSelectDiscipline,
    onSelectLevel,
    onSelectProgram,
    onChangeUnitSelected,
    onAddSelectedUnits,
    onChangeTimeSelected,
    selectedTime,
    optionsTimes,
  };
}
